/**
 * Shared prompt templates for on-device LLM inference.
 * Single source of truth — used by llm.web.ts and llm.native.ts.
 */

export function validatePrompt(entityType: string, text: string): string {
  switch (entityType) {
    case 'vision':
      return `Evaluate this vision statement. Is it aspirational, clear, and inspiring? Vision: "${text}"`;
    case 'goal':
      return `Evaluate this goal using SMART criteria (Specific, Measurable, Achievable, Relevant, Time-bound). Goal: "${text}"`;
    case 'activity':
      return `Evaluate this activity. Is it actionable (starts with a verb), clearly scoped, and completable in one session? Activity: "${text}"`;
    default:
      return `Evaluate this planning item: "${text}"`;
  }
}

export function suggestPrompt(entityType: string, text: string): string {
  switch (entityType) {
    case 'vision':
      return `Write 2-3 improved versions of this vision description in 2-3 sentences each. Each should paint a vivid mental image of the desired future, convey deeper purpose and meaning, and describe an ongoing way of living rather than a destination to reach. Keep each version concise — no more than 3 sentences.

Vision: "${text}"

Respond with a valid JSON array only. No markdown, no explanation:
[{"text":"Improved vision description in 2-3 sentences","reasoning":"What makes this version better"}]`;
    case 'goal':
      return `Write 2-3 improved versions of this goal in 2-3 sentences each. Make them SMART (Specific, Measurable, Achievable, Relevant, Time-bound). Keep each version concise — no more than 3 sentences.

Goal: "${text}"

Respond with a valid JSON array only. No markdown, no explanation:
[{"text":"Improved goal version in 2-3 sentences","reasoning":"What makes this version better"}]`;
    case 'activity':
      return `Write 2-3 improved versions of this activity. Make them more actionable with clear verbs and outcomes. Keep each version concise — 1-2 sentences.

Activity: "${text}"

Respond with a valid JSON array only. No markdown, no explanation:
[{"text":"Improved activity version in 1-2 sentences","reasoning":"What makes this version better"}]`;
    default:
      return `Suggest 2-3 improved versions of: "${text}"

Respond with a valid JSON array only. No markdown, no explanation:
[{"text":"Improved version","reasoning":"What makes this version better"}]`;
  }
}

export function splitActivityPrompt(activityText: string, estimatedMinutes: number): string {
  return `Break this ${estimatedMinutes}-minute activity into 2-4 smaller sub-activities. Each should start with a verb and have a time estimate. Activity: "${activityText}"`;
}

export function reflectPrompt(scoreSummary: string): string {
  return `You are a supportive productivity coach. Based on the following daily score summary, write a brief 2-3 sentence reflection about the day. Be encouraging but honest. Then list 1-3 highlights as bullet points.

${scoreSummary}

Format:
[Your reflection paragraph]
Highlights:
- [highlight 1]
- [highlight 2]`;
}

export function explainScorePrompt(scoreSummary: string): string {
  return `Explain this daily productivity score in plain English. Be concise (3-4 sentences). Focus on what drove the score and one actionable tip.

${scoreSummary}`;
}

export function goalCoveragePrompt(visionText: string, goalTitles: string[]): string {
  const goalList = goalTitles.map((g, i) => `${i + 1}. ${g}`).join('\n');
  return `Analyze this vision and its goals. Identify 3-5 key dimensions (aspects/areas) of the vision. For each dimension, determine if existing goals cover it or if it is a gap.

Vision: "${visionText}"

Goals:
${goalList}

Respond with a valid JSON object only. No markdown, no explanation. Use this exact structure:
{"dimensions":[{"name":"Financial Health","status":"covered","goal_indices":[1,3]},{"name":"Community Impact","status":"gap"}],"summary":"Strong coverage on finances but community impact needs attention."}

Rules:
- "status" must be exactly "covered" or "gap"
- "goal_indices" lists which goal numbers (1-based) cover that dimension (only for covered)
- Do not include "goal_indices" for gap dimensions`;
}

export function generateGapGoalsPrompt(visionTitle: string, gapDimensions: string[]): string {
  const gaps = gapDimensions.map((d, i) => `${i + 1}. ${d}`).join('\n');
  const today = new Date().toISOString().split('T')[0];
  return `Generate one SMART goal for each gap dimension to support this vision.

Vision: "${visionTitle}"

Gap dimensions:
${gaps}

Respond with a valid JSON array only. No markdown, no explanation. Use this exact structure:
[{"title":"Launch monthly volunteering program","description":"Organize and host monthly community service events to build local partnerships and increase community engagement from 0 to 12 events per year.","measures":[{"name":"Events hosted","unit":"count","start_value":0,"target_value":12}],"milestone_count":4,"start_date":"${today}","end_date":"2027-03-10"}]

Rules:
- One goal object per gap dimension, in the same order
- "title": concise, starts with a verb
- "description": 2-3 sentence SMART description (Specific, Measurable, Achievable, Relevant, Time-bound)
- "measures": array of 1-2 objects with "name", "unit", "start_value" (number), "target_value" (number)
- "milestone_count": 4 (quarterly), 6 (bi-monthly), or 12 (monthly)
- "start_date": "${today}"
- "end_date": appropriate YYYY-MM-DD date`;
}

export function activityCoveragePrompt(goalTitle: string, goalDescription: string, activityTitles: string[]): string {
  const actList = activityTitles.map((a, i) => `${i + 1}. ${a}`).join('\n');
  return `Analyze this goal and its activities. Identify 3-5 key action areas needed to achieve the goal. For each area, determine if existing activities cover it or if it is a gap.

Goal: "${goalTitle}"
Description: "${goalDescription}"

Activities:
${actList}

Respond with a valid JSON object only. No markdown, no explanation. Use this exact structure:
{"dimensions":[{"name":"Research & Planning","status":"covered","activity_indices":[1,3]},{"name":"Skill Building","status":"gap"}],"summary":"Good coverage on research but skill-building activities are missing."}

Rules:
- "status" must be exactly "covered" or "gap"
- "activity_indices" lists which activity numbers (1-based) cover that area (only for covered)
- Do not include "activity_indices" for gap dimensions`;
}

export function generateGapActivitiesPrompt(goalTitle: string, goalDescription: string, gapDimensions: string[]): string {
  const gaps = gapDimensions.map((d, i) => `${i + 1}. ${d}`).join('\n');
  return `Generate 1-2 actionable activities for each gap area to support this goal.

Goal: "${goalTitle}"
Description: "${goalDescription}"

Gap areas:
${gaps}

Respond with a valid JSON array only. No markdown, no explanation. Use this exact structure:
[{"title":"Research competitor pricing models","description":"Analyze 5 competitor pricing strategies and document findings in a comparison spreadsheet.","estimated_minutes":90,"relative_start_days":0}]

Rules:
- Each activity title starts with a verb
- "description": 1-2 sentence description of the activity
- "estimated_minutes": realistic time estimate (15-240 minutes)
- "relative_start_days": how many days from now to schedule (0 = today, 7 = next week, etc.)`;
}

/**
 * System-level prompt prefix for web/Android.
 * iOS uses @Generable structured output so doesn't need format instructions.
 */
export const FORMAT_INSTRUCTIONS = {
  validate: `Reply with VALID or INVALID, then list issues starting with -, then Recommendation: (one sentence), then Analysis: (one sentence).`,
  suggest: `Respond with valid JSON only. No markdown code fences.`,
  splitActivity: `Format each as: N. [subtask] (Xm)`,
  reflect: `Write a 2-3 sentence reflection, then list highlights starting with -.`,
  explainScore: `Explain in 3-4 concise sentences.`,
  goalCoverage: `Respond with valid JSON only. No markdown code fences.`,
  generateGapGoals: `Respond with a valid JSON array only. No markdown code fences.`,
  activityCoverage: `Respond with valid JSON only. No markdown code fences.`,
  generateGapActivities: `Respond with a valid JSON array only. No markdown code fences.`,
} as const;
